/**
 * Product page "Frequently Bought Together" — GA4 measurement.
 *
 * The block's add to cart reaches the shop's pixels as one ordinary add_to_cart (see the bulk
 * add in sections/main-product-custom.liquid), so nothing in GA tells it apart from the main
 * buy button. This file adds the three events that do:
 *
 *   fbt_view          the block >= half on screen for 1s, once per page load   item_count
 *   fbt_click         a click inside the block                                 click_target + selected_count
 *                       click_target: add_button | checkbox | product_link
 *   fbt_add_to_cart   the block's add to cart succeeded                        item_count
 *
 * Loaded by the section only when the block is rendered. Events go to the gtag the Shopify
 * Google & YouTube app installs in the page (same client and session as its page_view), once
 * that tag has configured the property; nothing here loads or configures a second copy, and a
 * visitor the app never tags is never tagged here either. Same transport as
 * assets/halloween-tracking.js, kept separate because that file is idle off the campaign.
 */
(() => {
 const MEASUREMENT_ID = 'G-BQPRTYRWS5';
 const BLOCK = '.pdp-fbt';
 const DWELL_MS = 1000;
 const TAG_POLL_MS = 500;
 const TAG_WAIT_MS = 30000;
 const TAG_SETTLE_MS = 1000;
 const SEND_GAP_MS = 300;
 const THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

 const block = document.querySelector(BLOCK);
 if (!block) return;

 // ------------------------------------------------------------------ transport

 const tagReady = () =>
  Array.isArray(window.dataLayer) &&
  window.dataLayer.some((command) => command && command[0] === 'config' && command[1] === MEASUREMENT_ID);

 // gtag.js only accepts the Arguments object itself, which an arrow function does not have.
 // eslint-disable-next-line func-names
 const gtag = function () {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
 };

 const outbox = [];
 let transport = 'idle'; // idle -> waiting -> ready | gone
 let pacing = false;

 const emit = ([name, params]) => gtag('event', name, { ...params, send_to: MEASUREMENT_ID });

 // One event per request, SEND_GAP_MS apart: events handed over in the same instant reach GA
 // with the same timestamp, and a funnel then orders them arbitrarily.
 const pump = () => {
  if (transport !== 'ready' || pacing || !outbox.length) return;
  emit(outbox.shift());
  pacing = true;
  setTimeout(() => {
   pacing = false;
   pump();
  }, SEND_GAP_MS);
 };

 const waitForTag = () => {
  transport = 'waiting';
  const started = Date.now();
  const poll = () => {
   if (tagReady()) {
    // A beat after the app's config, so the first of these never lands with its page_view.
    setTimeout(() => {
     transport = 'ready';
     pump();
    }, TAG_SETTLE_MS);
    return undefined;
   }
   if (Date.now() - started > TAG_WAIT_MS) {
    transport = 'gone';
    outbox.length = 0;
    return undefined;
   }
   return setTimeout(poll, TAG_POLL_MS);
  };
  poll();
 };

 const send = (name, params) => {
  if (transport === 'gone') return;
  outbox.push([name, params]);
  if (transport === 'idle') waitForTag();
  pump();
 };

 // A product link leaves the page with its own click still queued: send whatever is left now.
 window.addEventListener('pagehide', () => {
  if (transport === 'gone' || !tagReady()) return;
  transport = 'ready';
  outbox.splice(0).forEach(emit);
 });

 // ------------------------------------------------------------------ exposure

 const cardCount = (selector) => block.querySelectorAll(selector).length;

 // A block taller than the screen can never be half visible: half a screen of it counts too.
 const onScreen = (entry) =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= (entry.rootBounds?.height || window.innerHeight) * 0.5);

 if (typeof IntersectionObserver !== 'undefined') {
  let dwell = 0;
  const observer = new IntersectionObserver(
   ([entry]) => {
    if (!onScreen(entry)) {
     clearTimeout(dwell);
     dwell = 0;
    } else if (!dwell) {
     dwell = setTimeout(() => {
      observer.disconnect();
      send('fbt_view', { item_count: cardCount('.pdp-fbt__card') });
     }, DWELL_MS);
    }
   },
   { threshold: THRESHOLDS }
  );
  observer.observe(block);
 }

 // ------------------------------------------------------------------ clicks

 // Capture phase: the section stops propagation on the checkbox, and the selection has to be
 // read before its handler toggles it.
 document.addEventListener(
  'click',
  (event) => {
   const target = event.target instanceof Element ? event.target : null;
   if (!target || !target.closest(BLOCK)) return;

   const button = target.closest('[data-fbt-add-btn]');
   // A click on the busy button is dropped by the section as well.
   if (button && button.getAttribute('aria-busy') === 'true') return;

   const clickTarget = button
    ? 'add_button'
    : target.closest('.pdp-fbt__card-check')
    ? 'checkbox'
    : target.closest('.pdp-fbt__card-img, .pdp-fbt__card-info')
    ? 'product_link'
    : '';
   if (!clickTarget) return;

   send('fbt_click', { click_target: clickTarget, selected_count: cardCount('.pdp-fbt__card-check.is-checked') });
  },
  true
 );

 // Dispatched by the section once the block's add to cart has gone through.
 document.addEventListener('fbt:added', (event) => send('fbt_add_to_cart', { item_count: event.detail?.count || 0 }));
})();
